import type { InfiniteData, QueryClient } from "@tanstack/react-query";

import type { PaginatedResponse } from "@/shared/http";

import type { FileRecord } from "../FileRecord";

import { fileQueryKeys } from "./fileQueryKeys";

/** What {@link useOrgFileSearch} stores: one entry per narrowing, paginated. */
type SearchCache = InfiniteData<
  PaginatedResponse<FileRecord>,
  string | undefined
>;

/** The file to rewrite, and the org whose searches might be holding it. */
export interface CachedFileRecordTarget {
  fileId: string;
  orgId: string;
}

/**
 * Rewrite one file's cached row wherever an org file search is holding it.
 *
 * A record is cached in two unrelated places — `detail(fileId)`, and inside
 * whichever search pages happened to return it — and a change to the file makes
 * both wrong. Invalidating the detail key does nothing for the search entries,
 * which is how a converted file goes on rendering as unconverted in a picker
 * that is still on screen.
 *
 * ## Why this writes rather than invalidates
 *
 * The obvious fix is to invalidate `lists(orgId)` on the same event, which is
 * what {@link useResetVisualizationAsset} does. It is unreliable here.
 * `POST /v1/files/query`, which backs every one of these searches, is
 * explicitly **eventually consistent**, and the web-ui sends neither an API
 * version nor a consistency header — so the refetch an invalidation triggers
 * may be served by a replica that has not yet seen the write, re-caching the
 * stale row with nothing left to fire a second time. It would work most of the
 * time, which for a caching bug is the worst of both.
 *
 * Callers of this reach it holding a value they know is post-write — read back
 * from `GET /v1/files/record/<file_id>`, which never opts into the replica, or
 * built locally from a request the platform has already accepted. Propagating
 * that beats asking a replica to say it again: there is no read to race, the
 * row updates in the same frame, and an infinite query is not made to refetch
 * every page it has loaded.
 *
 * Only rows that are already cached are rewritten. Nothing is inserted: a
 * search's membership is the server's to decide, and a row this org's search
 * never returned is not one this function knows belongs there.
 *
 * @param queryClient the client holding the caches
 * @param target the file to rewrite, and the org to look for it in
 * @param update the record it should become, given the one that is cached
 */
export function patchCachedFileRecords(
  queryClient: QueryClient,
  target: CachedFileRecordTarget,
  update: (record: FileRecord) => FileRecord,
): void {
  queryClient.setQueriesData<SearchCache>(
    { queryKey: fileQueryKeys.searches(target.orgId) },
    (cached) => patchPages(cached, target.fileId, update),
  );
}

/**
 * The same pages, with the one matching row replaced.
 *
 * Returns the argument **by identity** when no page held the file. `setQueriesData`
 * writes back whatever it is handed, so a freshly-built object for an entry that
 * did not change would re-render every row of every other search this org has
 * cached — for a file none of them are showing.
 */
function patchPages(
  cached: SearchCache | undefined,
  fileId: string,
  update: (record: FileRecord) => FileRecord,
): SearchCache | undefined {
  if (cached === undefined) {
    return cached;
  }

  let patched = false;
  const pages = cached.pages.map((page) => {
    const index = page.items.findIndex((item) => item.file_id === fileId);
    if (index === -1) {
      return page;
    }
    patched = true;
    const items = [...page.items];
    items[index] = update(items[index]);
    return { ...page, items };
  });

  return patched ? { ...cached, pages } : cached;
}
